"use client";

import { c as _c } from "react/compiler-runtime";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import DropMobileMenuHandler from "@/components/waves/drops/DropMobileMenuHandler";
import { getRankHoverBorderClass } from "@/components/waves/drops/dropRankStyles";
import WaveDropReactions from "@/components/waves/drops/WaveDropReactions";
import type { DropInteractionParams } from "@/components/waves/drops/drop.types";
import { DropLocation } from "@/components/waves/drops/drop.types";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { useCallback, useState } from "react";
import MemeDropActions from "./meme-participation-drop/MemeDropActions";
import MemeDropArtistInfo from "./meme-participation-drop/MemeDropArtistInfo";
import MemeDropDescription from "./meme-participation-drop/MemeDropDescription";
import MemeDropHeader from "./meme-participation-drop/MemeDropHeader";
import MemeDropVoteStats from "./meme-participation-drop/MemeDropVoteStats";
import MemeDropTraits from "./MemeDropTraits";
interface MemeParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly showInteractions?: boolean | undefined;
}

// Border styling based on rank
const getBorderClasses = (drop: ExtendedDrop, isActiveDrop: boolean) => {
  const rank = typeof drop.rank === "number" && drop.rank <= 3 ? drop.rank : null;
  const baseClasses = "tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-transition-all tw-duration-200 tw-ease-out tw-overflow-hidden";
  if (isActiveDrop) {
    return `${baseClasses} desktop-hover:hover:tw-border-[#3CCB7F]/40 tw-bg-[#3CCB7F]/5`;
  }
  if (rank === 1) {
    return `${baseClasses} ${getRankHoverBorderClass(1)}`;
  }
  if (rank === 2) {
    return `${baseClasses} ${getRankHoverBorderClass(2)}`;
  }
  if (rank === 3) {
    return `${baseClasses} ${getRankHoverBorderClass(3)}`;
  }
  return `${baseClasses} tw-border-iron-800`;
};
export default function MemeParticipationDrop(t0) {
  const $ = _c(73);
  const {
    drop,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    showInteractions: t1
  } = t0;
  const showInteractions = t1 === undefined ? true : t1;
  const [isVotingModalOpen, setIsVotingModalOpen] = useState(false);
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isMobile = useIsMobileDevice();
  const isMobileScreen = useIsMobileScreen();
  const title = drop.metadata.find(_temp)?.data_value ?? "Artwork Title";
  const description = drop.metadata.find(_temp2)?.data_value ?? "This is an artwork submission for The Memes collection.";
  let t2;
  if ($[0] !== drop.parts) {
    t2 = drop.parts.at(0)?.media.at(0);
    $[0] = drop.parts;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const artworkMedia = t2;
  let t3;
  if ($[2] !== drop || $[3] !== isActiveDrop) {
    t3 = getBorderClasses(drop, isActiveDrop);
    $[2] = drop;
    $[3] = isActiveDrop;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const borderClasses = t3;
  let t4;
  if ($[5] !== drop || $[6] !== onReply) {
    t4 = () => {
      onReply({
        drop,
        partId: drop.parts[0]?.part_id
      });
    };
    $[5] = drop;
    $[6] = onReply;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleOnReply = t4;
  let t5;
  if ($[8] !== drop) {
    t5 = <MemeDropArtistInfo drop={drop} />;
    $[8] = drop;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== title) {
    t6 = <MemeDropHeader title={title} />;
    $[10] = title;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== description) {
    t7 = <MemeDropDescription description={description} />;
    $[12] = description;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <div className="tw-mt-2 tw-flex tw-flex-col sm:tw-ml-[3.25rem] sm:tw-mt-1.5">{t6}{t7}</div>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t5 || $[18] !== t8) {
    t9 = <div className="tw-p-4">{t5}{t8}</div>;
    $[17] = t5;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== artworkMedia || $[21] !== location) {
    t10 = artworkMedia && <div className={`tw-flex tw-h-96 tw-justify-center ${location === DropLocation.WAVE ? "tw-bg-iron-800/30" : "tw-bg-iron-900/40"}`}><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} /></div>;
    $[20] = artworkMedia;
    $[21] = location;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== drop) {
    t11 = <div className="tw-px-4 tw-py-4"><MemeDropTraits drop={drop} /></div>;
    $[23] = drop;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t9) {
    t12 = <>{t9}{t10}{t11}</>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t9;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const content = t12;
  const t13 = `tw-group tw-relative tw-w-full ${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""}`;
  const t14 = `${borderClasses} ${location === DropLocation.WAVE ? "tw-bg-iron-900/80" : "tw-bg-iron-950"}`;
  let t15;
  if ($[29] !== content || $[30] !== drop || $[31] !== handleOnReply || $[32] !== showInteractions || $[33] !== showReplyAndQuote) {
    t15 = showInteractions ? <DropMobileMenuHandler drop={drop} showReplyAndQuote={showReplyAndQuote} onReply={handleOnReply}>{content}</DropMobileMenuHandler> : content;
    $[29] = content;
    $[30] = drop;
    $[31] = handleOnReply;
    $[32] = showInteractions;
    $[33] = showReplyAndQuote;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== drop.context_profile_context || $[36] !== drop.raters_count || $[37] !== drop.rating || $[38] !== drop.rating_prediction || $[39] !== drop.top_raters || $[40] !== drop.wave.voting_credit_type) {
    t16 = <div className="tw-px-4 @[700px]:tw-px-0"><MemeDropVoteStats current={drop.rating} projected={drop.rating_prediction} votingCreditType={drop.wave.voting_credit_type} ratersCount={drop.raters_count} topVoters={drop.top_raters} userContext={drop.context_profile_context} /></div>;
    $[35] = drop.context_profile_context;
    $[36] = drop.raters_count;
    $[37] = drop.rating;
    $[38] = drop.rating_prediction;
    $[39] = drop.top_raters;
    $[40] = drop.wave.voting_credit_type;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== canShowVote || $[43] !== drop || $[44] !== showInteractions) {
    t17 = canShowVote && showInteractions && <div className="tw-flex tw-w-full tw-justify-center tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800 tw-px-6 tw-pt-4 @[700px]:tw-w-auto @[700px]:tw-border-none @[700px]:tw-px-0 @[700px]:tw-pt-0"><div onClick={_temp3}><VotingModalButton drop={drop} onClick={() => setIsVotingModalOpen(true)} /></div></div>;
    $[42] = canShowVote;
    $[43] = drop;
    $[44] = showInteractions;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t16 || $[47] !== t17) {
    t18 = <div className="tw-flex tw-flex-col tw-justify-between tw-gap-3 tw-pb-4 @[700px]:tw-flex-row @[700px]:tw-items-center @[700px]:tw-gap-y-0 @[700px]:tw-px-4">{t16}{t17}</div>;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== drop || $[50] !== showInteractions) {
    t19 = showInteractions && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-px-4 tw-pb-4"><WaveDropReactions drop={drop} /></div>;
    $[49] = drop;
    $[50] = showInteractions;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== drop || $[53] !== handleOnReply || $[54] !== isMobile || $[55] !== showInteractions || $[56] !== showReplyAndQuote) {
    t20 = showInteractions && <div className="tw-absolute tw-right-4 tw-top-2"><MemeDropActions drop={drop} isMobile={isMobile} showReplyAndQuote={showReplyAndQuote} onReply={handleOnReply} /></div>;
    $[52] = drop;
    $[53] = handleOnReply;
    $[54] = isMobile;
    $[55] = showInteractions;
    $[56] = showReplyAndQuote;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== t14 || $[59] !== t15 || $[60] !== t18 || $[61] !== t19 || $[62] !== t20) {
    t21 = <div className={t14}>{t15}{t18}{t19}{t20}</div>;
    $[58] = t14;
    $[59] = t15;
    $[60] = t18;
    $[61] = t19;
    $[62] = t20;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== drop || $[65] !== isMobileScreen || $[66] !== isVotingModalOpen || $[67] !== showInteractions) {
    t22 = showInteractions && (isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} />);
    $[64] = drop;
    $[65] = isMobileScreen;
    $[66] = isVotingModalOpen;
    $[67] = showInteractions;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  let t23;
  if ($[69] !== t13 || $[70] !== t21 || $[71] !== t22) {
    t23 = <div className="tw-w-full tw-@container"><div className={t13}>{t21}{t22}</div></div>;
    $[69] = t13;
    $[70] = t21;
    $[71] = t22;
    $[72] = t23;
  } else {
    t23 = $[72];
  }
  return t23;
}
function _temp3(e) {
  return e.stopPropagation();
}
function _temp2(m_0) {
  return m_0.data_key === "description";
}
function _temp(m) {
  return m.data_key === "title";
}